import { useEffect, useRef, useState } from "react";
import { Camera, CheckCircle2, Images, Loader2, Star } from "lucide-react";
import { api } from "../../lib/api";
import { GreatBaobab } from "./GreatBaobab";
import { BaoRig } from "../../game/bao/BaoRig";
import { duration, sample } from "../../game/bao/rig";
import { useBaoModel } from "../three/useBaoModel";
import { Clip3D } from "../three/Clip3D";
import { shareCanvas } from "../three/snapshot";

const svgImage = (svg) => new Promise((res, rej) => {
  const s = svg.cloneNode(true);
  s.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  const img = new Image();
  img.onload = () => res(img); img.onerror = rej;
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(s.outerHTML)}`;
});
const fit = (g, src, x, y, w, h, sw = src.width, sh = src.height) => { const r = Math.min(w / sw, h / sh); g.drawImage(src, x + (w - sw * r) / 2, y + (h - sh * r), sw * r, sh * r); };

async function compose(box, { seeds, stars, title }) {
  const W = 1600, H = 2000, c = document.createElement("canvas"), g = c.getContext("2d");
  c.width = W; c.height = H;
  const sky = g.createLinearGradient(0, 0, 0, H);
  sky.addColorStop(0, "#9ED9C9"); sky.addColorStop(0.62, "#F3E3B8"); sky.addColorStop(1, "#E7CB93");
  g.fillStyle = sky; g.fillRect(0, 0, W, H);
  g.fillStyle = "#FFE29A"; g.globalAlpha = 0.5; g.beginPath(); g.arc(W * 0.78, 360, 170, 0, Math.PI * 2); g.fill(); g.globalAlpha = 1;
  fit(g, await svgImage(box.querySelector("[data-testid=photo-baobab]")), 120, 220, W - 240, 1180, 240, 230);
  const bao3d = box.querySelector("canvas"), bao2d = box.querySelector("[data-testid=photo-bao-2d] svg");
  if (bao3d) fit(g, bao3d, W * 0.2, 760, W * 0.6, 1020);
  else if (bao2d) fit(g, await svgImage(bao2d), W * 0.28, 820, W * 0.44, 960, 300, 460);
  g.fillStyle = "#0A2540"; g.fillRect(0, 0, W, 170); g.fillRect(0, H - 190, W, 190);
  g.fillStyle = "#17A2A2"; g.fillRect(0, 170, W, 8); g.fillRect(0, H - 198, W, 8);
  g.fillStyle = "#FFFFFF"; g.font = "600 64px Fraunces, Georgia, serif"; g.fillText("Bao fête sa séance !", 70, 110);
  g.fillStyle = "#9FE7DE"; g.font = "700 30px Manrope, sans-serif"; g.textAlign = "right";
  g.fillText(new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }), W - 70, 104);
  g.textAlign = "left"; g.fillStyle = "#FFE29A"; g.font = "800 46px Manrope, sans-serif";
  g.fillText(`${"★".repeat(Math.min(stars, 9))}  ${stars} étoile${stars > 1 ? "s" : ""}  ·  ${seeds} Graine${seeds > 1 ? "s" : ""} de Vie`, 70, H - 100);
  g.fillStyle = "#D7F5F1"; g.font = "600 26px Manrope, sans-serif"; g.fillText(`${title} · BAOBAB ADVENTURES · MOVE. EXPLORE. RECOVER.`, 70, H - 46);
  return c;
}
const fileName = () => `bao-seance-${new Date().toISOString().slice(0, 10)}.png`;

/** Saves the photo to the patient's album (Emergent object storage via backend) once per mount. */
const useAutoSave = (box, model, pid, meta) => {
  const [saved, setSaved] = useState(null), done = useRef(false);
  useEffect(() => {
    if (!pid || model === undefined || done.current) return;
    done.current = true; setSaved("saving");
    const t = setTimeout(async () => {
      try {
        const c = await compose(box.current, meta), blob = await new Promise((r) => c.toBlob(r, "image/png")), fd = new FormData();
        fd.append("file", blob, fileName()); fd.append("title", meta.title); fd.append("seeds", meta.seeds); fd.append("stars", meta.stars);
        await api.post(`/patients/${pid}/photos`, fd); setSaved("ok");
      } catch { setSaved("error"); }
    }, 2600);
    return () => clearTimeout(t);
  }, [pid, model]); // eslint-disable-line react-hooks/exhaustive-deps
  return saved;
};

/** End-of-session studio photo: Bao celebrating in front of the patient's Great Baobab (its real growth), date + stars; share or download. */
export const CelebrationPhoto = ({ seeds = 0, fresh = 0, stars = 0, title = "Séance du jour", pid, onAlbum }) => {
  const box = useRef(null), model = useBaoModel();
  const [busy, setBusy] = useState(false);
  const saved = useAutoSave(box, model, pid, { seeds, stars, title });
  const share = async () => { setBusy(true); try { await shareCanvas(await compose(box.current, { seeds, stars, title }), fileName(), "Ma séance avec Bao · Baobab Adventures"); } finally { setBusy(false); } };
  return (
    <div className="rounded-3xl bg-white p-3 shadow-sm" data-testid="celebration-photo">
      <div ref={box} className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-gradient-to-b from-[#9ED9C9] via-[#F3E3B8] to-[#E7CB93]">
        <div className="absolute inset-x-[6%] top-[4%] [&>svg]:h-auto [&>svg]:w-full"><GreatBaobab seeds={seeds} fresh={fresh} size={400} testId="photo-baobab" /></div>
        <div className="absolute inset-x-[20%] bottom-0 h-[62%]">
          {model ? <Clip3D model={model} clip="celebrate" preserve testId="photo-bao-3d" /> : model === null && (
            <div className="h-full" data-testid="photo-bao-2d"><BaoRig pose={sample("jump", duration("jump") * 0.45)} view="front" width="100%" /></div>
          )}
        </div>
        <span className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-[#0A2540]/85 px-2.5 py-1 text-xs font-bold text-[#FFE29A]" data-testid="photo-stars"><Star className="h-3.5 w-3.5 fill-current" />{stars}</span>
      </div>
      <button data-testid="share-celebration-photo" onClick={share} disabled={busy || model === undefined}
        className="mt-3 flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[#0A2540] text-base font-extrabold text-white disabled:opacity-50">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}Partager ma photo de séance
      </button>
      {saved && (
        <div className="mt-2 flex items-center justify-between gap-2 px-1 text-xs font-bold text-[#0F7C7C]" data-testid="photo-saved" data-status={saved}>
          <span className="flex items-center gap-1.5">{saved === "ok" ? <CheckCircle2 className="h-4 w-4" /> : saved === "saving" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {saved === "ok" ? "Ajoutée à ton album" : saved === "saving" ? "Enregistrement dans ton album…" : "Album indisponible pour le moment"}</span>
          {onAlbum && saved === "ok" && <button data-testid="open-album" onClick={onAlbum} className="flex items-center gap-1 rounded-full bg-[#EAF7F5] px-3 py-1.5"><Images className="h-3.5 w-3.5" />Mon album</button>}
        </div>
      )}
    </div>
  );
};
